import { useCallback, useRef } from 'react';

// COM-637 code review: guards a set of mutually-exclusive one-shot actions
// (Accept/Decline on SourceDiscoveryDialog) so only the first click that
// arrives actually runs. A React `disabled` prop/state is NOT sufficient by
// itself: setState only takes effect on the *next* render, so a second click
// (double-click, double-tap, or a click landing while the dialog is still
// fading out on its default MUI transition) can still fire before that
// render commits - see ClientDataDialog's handleAcceptDiscovery, where firing
// twice means calling `startSourceDiscovery()` (one paid OpenAI call per
// category) twice. Checking a ref synchronously at the very top of the
// handler closes that window regardless of render timing.
//
// `reset()` must be called explicitly (by the caller, when a fresh question
// is about to be shown) rather than automatically expiring - the guard has
// no notion of "one dialog open cycle" on its own.
export function useAnswerOnce() {
  const answeredRef = useRef(false);

  const reset = useCallback(() => {
    answeredRef.current = false;
  }, []);

  const guard = useCallback(
    (action: () => void) => () => {
      if (answeredRef.current) return;
      answeredRef.current = true;
      action();
    },
    []
  );

  return { guard, reset };
}
